import React from 'react';

import { Box, Typography } from '@mui/material';
import _ from 'es-toolkit/compat';

import { Button, FullscreenLoader } from 'src/components/common';
import { Field, Form, NumberField, ToggleButtonGroupField } from 'src/components/finalForm';
import { REVENUE_SHARE_OPTIONS } from 'src/constants/billing';
import { useInitialBillingRules } from 'src/hooks/account/useInitialBillingRules';
import { useNotification } from 'src/hooks/app';
import { SxProp } from 'src/types/mui';
import { validateWithYupSchema } from 'src/utils/yup';

import {
  EmployerBillingRulesFormFieldsValues,
  EmployerInitialBillingFormFields,
  RevenueFieldNameTypeMap,
} from './types';
import { getEmployerInitialBillingRulesInitialValues } from './utils/getInitialBillingInitialValues';
import { EmployerInitialBillingRulesValidationSchema } from './utils/validation';

export const EmployerInitialBillingRules: React.FC = () => {
  const { createApolloContext } = useNotification();
  const {
    initialBillingRules,
    updateInitialBillingRules,
    updating,
    loading: isInitialBillingRulesLoading,
  } = useInitialBillingRules();

  const initialValues = getEmployerInitialBillingRulesInitialValues(initialBillingRules);

  const onSubmit = React.useCallback(
    async ({ revenueShareType, revenueShare }: EmployerBillingRulesFormFieldsValues) => {
      if (!revenueShareType) return;

      try {
        await updateInitialBillingRules({
          data: {
            revenueShareType,
            [_.get(RevenueFieldNameTypeMap, revenueShareType)]: revenueShare,
          },
          context: createApolloContext({
            successMessage: 'The changes have been saved',
          }),
        });
      } catch (error) {
        console.error(error);
      }
    },
    [createApolloContext, updateInitialBillingRules],
  );

  const loading = updating || isInitialBillingRulesLoading;

  if (loading) {
    return <FullscreenLoader />;
  }

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={validateWithYupSchema(EmployerInitialBillingRulesValidationSchema)}
    >
      {({ form, dirty, submitting, valid }) => (
        <Box mb={4}>
          <Typography fontSize="16px" variant="subtitle1">
            Revenue Share
          </Typography>
          <Typography fontSize="16px" color="text.secondary" mb="27px" variant="body1">
            Is a percent or flat amount that Employer gets from any request. Changes will be applied
            instantly.
          </Typography>
          <Box display="flex" justifyContent="space-between" gap="16px">
            <Field
              name={EmployerInitialBillingFormFields.revenueShare}
              required
              fullWidth
              label="Verification of Income"
              component={NumberField}
            />
            <Field
              name={EmployerInitialBillingFormFields.revenueShareType}
              component={ToggleButtonGroupField}
              options={REVENUE_SHARE_OPTIONS}
              exclusive
              sx={buttonGroupStyles}
              withAbilityToChooseNothing={false}
            />
          </Box>
          <Box display="flex" justifyContent="center" alignItems="center" gap="24px" mt={5}>
            <Button
              color="error"
              disabled={!dirty}
              size="medium"
              type="reset"
              variant="outlined"
              onClick={() => form.restart()}
            >
              Reset
            </Button>
            <Button
              color="primary"
              disabled={!valid || !dirty || submitting}
              loading={submitting}
              variant="contained"
              type="submit"
            >
              Save changes
            </Button>
          </Box>
        </Box>
      )}
    </Form>
  );
};

const buttonGroupStyles: SxProp = {
  height: '100%',
  maxHeight: '60px',
};
